import {action, computed, makeObservable, observable} from "mobx";
import FormSet from "../../../forms/fields/FormSet";
import DeliverySiteOnboardingRequestForm from "../../../forms/fields/DeliverySiteOnboardingRequestForm";
import {default as handleErrorToast} from "../../../components/toast/ErrorToast";
import {default as handleSuccessToast} from "../../../components/toast/SuccessToast";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import CustomerOnboardingPanelFeature from "./CustomerOnboardingPanelFeature";

export default class CustomerOnboardingDeliverySitesOnboardingRequestsFeature extends CustomerOnboardingPanelFeature {

    constructor(application, generalFeature) {
        super(application, generalFeature);
        this._deliverySitesOnboardingRequestsFormSet = null;

        makeObservable(this, {
            _deliverySitesOnboardingRequestsFormSet: observable,
            deliverySitesOnboardingRequestsFormSet: computed,
            addDeliverySiteOnboardingRequest: action,
            removeDeliverySiteOnboardingRequest: action,
            editOrCreateDeliverySiteOnboardingRequest: action,
        })
    }

    async load() {
        this.createDeliverySitesOnboardingRequestsFormSet();
        this.finishLoading();
    }

    async formChanged(deliverySiteOnboardingRequest){
        await this.editOrCreateDeliverySiteOnboardingRequest(deliverySiteOnboardingRequest)
    }

    createDeliverySitesOnboardingRequestsFormSet() {
        const deliverySitesOnboardingRequests = this.customerOnboarding().request.deliverySitesOnboardingRequests;
        this._deliverySitesOnboardingRequestsFormSet = new FormSet([], this);
        deliverySitesOnboardingRequests.forEach(deliverySiteOnboardingRequest => this.addDeliverySiteOnboardingRequestFor(deliverySiteOnboardingRequest));
    }

    addDeliverySiteOnboardingRequest() {
        const deliverySiteOnboardingRequestForm = DeliverySiteOnboardingRequestForm.newEmpty(this, this.customerOnboarding().configuration);
        this._deliverySitesOnboardingRequestsFormSet.addForm(deliverySiteOnboardingRequestForm);
        return deliverySiteOnboardingRequestForm;
    }

    addDeliverySiteOnboardingRequestFor(deliverySitesOnboardingRequest) {
        const deliverySiteOnboardingRequestForm = DeliverySiteOnboardingRequestForm.newFor(deliverySitesOnboardingRequest, this, this.customerOnboarding().configuration);
        this._deliverySitesOnboardingRequestsFormSet.addForm(deliverySiteOnboardingRequestForm);
        return deliverySiteOnboardingRequestForm;
    }

    removeDeliverySiteOnboardingRequest(deliverySiteId) {
        this._deliverySitesOnboardingRequestsFormSet.removeForm(deliverySiteId);
    }

    createForm(){
        return this.createDeliverySitesOnboardingRequestsFormSet()
    }

    get deliverySitesOnboardingRequestsFormSet() {
        return this._deliverySitesOnboardingRequestsFormSet;
    }

    get form(){
        return this.deliverySitesOnboardingRequestsFormSet
    }

    async editOrCreateDeliverySiteOnboardingRequest(deliverySiteOnboardingRequest) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                await this.customerOnboardingHasChanged()
                const allIds = response.customerOnboarding().request.deliverySitesOnboardingRequests.map(deliverySiteOnboardingRequest => deliverySiteOnboardingRequest.id);
                this._deliverySitesOnboardingRequestsFormSet.updateEntityIdAfterEdition(deliverySiteOnboardingRequest, allIds)
                handleSuccessToast("Datos guardados correctamente.");
            },
            handlesError: (response) => {
                handleErrorToast("No se pudieron guardar los datos.");
            }
        });
        return this._application.apiClient().editOrCreateDeliverySitesOnboardingRequests(this.customerOnboarding().request.taxpayerIdentificationNumber,
            deliverySiteOnboardingRequest, responseHandler);
    }

    async deleteEntity(deliverySiteOnboardingRequest) {
        if (deliverySiteOnboardingRequest.id) {
            const responseHandler = new ApiResponseHandler({
                handlesSuccess: async (response) => {
                    await this.customerOnboardingHasChanged()
                    this.createDeliverySitesOnboardingRequestsFormSet()
                    handleSuccessToast("Punto de entrega eliminado correctamente.");
                },
                handlesError: (response) => {
                    handleErrorToast("No se pudo borrar el punto de entrega.");
                }
            });
            return this._application.apiClient().deleteDeliverySiteOnboardingRequest(deliverySiteOnboardingRequest.id, responseHandler);
        }
    }

    zones() {
        return [
            'CABA',
            'OESTE',
            'SUR',
            'NORTE',
            'NORTE EXTENDIDO PILAR',
            'NORTE EXTENDIDO ESCOBAR',
            'ROSARIO',
            'CORDOBA',
        ];
    }

    frequencies() {
        return [
            'LUNES',
            'MARTES',
            'MIÉRCOLES',
            'JUEVES',
            'VIERNES',
            'SÁBADO',
            'SIN DIA FIJOS',
        ];
    }

    optionKeyFor(label) {
        return label
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toUpperCase()
            .replace(/\s+/g, "_");
    }

    jurisdictions() {
        return [
            'Buenos Aires',
            'Catamarca',
            'Chaco',
            'Chubut',
            'Capital Federal',
            'Córdoba',
            'Corrientes',
            'Entre Ríos',
            'Formosa',
            'Jujuy',
            'La Pampa',
            'La Rioja',
            'Mendoza',
            'Misiones',
            'Neuquén',
            'Río Negro',
            'Salta',
            'San Juan',
            'San Luis',
            'Santa Cruz',
            'Santa Fe',
            'Santiago del Estero',
            'Tierra del Fuego',
            'Tucumán',
        ];
    }

    jurisdictionsFieldOptions() {
        return this.optionsFor(this.jurisdictions());
    }

    zonesFieldOptions() {
        return this.optionsFor(this.zones());
    }

    frequenciesFieldOptions() {
        return this.optionsFor(this.frequencies());
    }

    optionsFor(values) {
        return values.map(value => {
            return {label: value, value: value}
        });
    }

    zoneFrequencyMapping() {
        return {
            'CABA': ['MARTES', 'JUEVES', 'SABADO'],
            'OESTE': ['LUNES', 'JUEVES'],
            'SUR': ['MIERCOLES', 'VIERNES'],
            'NORTE': ['LUNES', 'MIERCOLES', 'VIERNES'],
            'NORTE EXTENDIDO': ['MARTES', 'VIERNES'],
            'NORTE EXTENDIDO PILAR': ['MIERCOLES', 'SABADO'],
            'NORTE EXTENDIDO ESCOBAR': ['MARTES', 'VIERNES'],
            'ROSARIO': ['VIERNES', 'SABADO'],
            'CORDOBA': ['SIN DIAS FIJOS'],
        }
    }

    chosenZoneFrequency(deliverySiteOnboardingRequestForm) {
        const chosenZone = deliverySiteOnboardingRequestForm.zone()
        if (chosenZone) {
            return this.zoneFrequencyMapping()[chosenZone]
        }
        return []
    }

    canEditDeliverySitesOnboardingRequests() {
        return !this.customerOnboarding().configuration.readonlyFields.includes('_delivery_site_onboarding_requests')
    }
}